import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { http } from "@/lib/api";

const W = 160, H = 44;

const Spark = ({ points, color, testId }) => {
  const xs = points.map((_, i) => (points.length === 1 ? W / 2 : (i * W) / (points.length - 1)));
  const y = (p) => H - 4 - ((p - 0) / 100) * (H - 8);
  const seg = [];
  let cur = [];
  points.forEach((p, i) => { if (p == null) { if (cur.length) seg.push(cur); cur = []; } else cur.push([xs[i], y(p)]); });
  if (cur.length) seg.push(cur);
  const all = points.map((p, i) => p != null && [xs[i], y(p)]).filter(Boolean);
  return (
    <svg viewBox={`-4 0 ${W + 8} ${H}`} className="w-full h-11" role="img" aria-label="Weekly grade trend" data-testid={testId}>
      <line x1="0" x2={W} y1={y(70)} y2={y(70)} stroke="#E6DEC3" strokeDasharray="3 3" />
      <polyline points={all.map((p) => p.join(",")).join(" ")} fill="none" stroke={color} strokeOpacity="0.25" strokeWidth="2" strokeDasharray="2 3" />
      {seg.map((s, k) => <polyline key={k} points={s.map((p) => p.join(",")).join(" ")} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />)}
      {all.map(([x, yy], k) => <circle key={k} cx={x} cy={yy} r={k === all.length - 1 ? 3.5 : 2} fill={color} />)}
    </svg>
  );
};

const Delta = ({ s }) => {
  if (s.previous == null) return <span className="text-xs text-stone-400">first week</span>;
  const d = s.latest - s.previous;
  const [Icon, cls] = d > 0 ? [TrendingUp, "text-[#2E7D4F]"] : d < 0 ? [TrendingDown, "text-[#A33224]"] : [Minus, "text-stone-500"];
  return <span className={`inline-flex items-center gap-1 text-xs font-medium ${cls}`} data-testid={`trend-delta-${s.id}`}><Icon className="w-3.5 h-3.5" />{d > 0 ? "+" : ""}{d}%</span>;
};

export const GradeTrends = ({ studentId = "me", refresh = 0 }) => {
  const [weeks, setWeeks] = useState(6);
  const [d, setD] = useState(null);
  useEffect(() => { http.get(`/coursework/${studentId}/trends`, { params: { weeks } }).then((r) => setD(r.data)).catch(() => setD({ subjects: [] })); }, [studentId, weeks, refresh]);
  return (
    <section className="mt-6 rounded-3xl bg-white/70 border border-[#E6DEC3] p-6" data-testid="grade-trends">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1"><p className="font-display text-2xl text-[#2C4A3E]">Grade trends</p><p className="text-xs text-stone-500">Weekly average on graded work: teacher assignments and school grades.</p></div>
        <div className="flex p-1 rounded-full bg-[#F1EADB]" role="tablist">
          {[6, 12].map((w) => <button key={w} role="tab" aria-selected={weeks === w} onClick={() => setWeeks(w)} data-testid={`trend-weeks-${w}`} className={`px-3 py-1.5 rounded-full text-xs transition-colors ${weeks === w ? "bg-[#2C4A3E] text-white" : "text-[#2C4A3E]"}`}>{w} weeks</button>)}
        </div>
      </div>
      {!d ? <p className="mt-6 text-sm text-stone-400">Loading…</p> : !d.subjects.length ? <p className="mt-6 text-sm text-stone-500" data-testid="grade-trends-empty">No graded work yet. Scores from teacher assignments and school grades you add will show here.</p> : (
        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {d.subjects.map((s) => (
            <div key={s.id} className="rounded-2xl bg-[#FDFBF7] border border-[#EEE8DA] p-4 bubble-in" data-testid={`trend-card-${s.id}`}>
              <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} /><span className="text-xs uppercase tracking-wider text-stone-500 flex-1 truncate">{s.short}</span><Delta s={s} /></div>
              <p className="font-display text-3xl text-[#1F3D33] mt-1" data-testid={`trend-latest-${s.id}`}>{s.latest}%</p>
              <Spark points={s.points} color={s.color} testId={`trend-spark-${s.id}`} />
              <p className="text-[10px] text-stone-400 mt-1">{s.count} graded item{s.count === 1 ? "" : "s"} · {weeks} weeks</p>
            </div>))}
        </div>)}
    </section>
  );
};
